import React from "react";
import { CalendarClock } from "lucide-react";
import { daysUntilChallengeStart } from "@/lib/challengeDay";
import { hebrewDaysPhrase } from "@/lib/hebrewPlural";

interface PreChallengeBannerProps {
  startDate: string;
  challengeTitle?: string;
}

/**
 * Banner shown on Home when the user is enrolled in a challenge that has
 * not started yet (current day < 1 — i.e., we are in the preparation
 * window or even before it).
 *
 * Renders a friendly countdown to `startDate`.
 */
export const PreChallengeBanner = React.memo(function PreChallengeBanner({
  startDate,
  challengeTitle,
}: PreChallengeBannerProps) {
  const daysLeft = daysUntilChallengeStart(startDate);

  const headline =
    daysLeft === 0
      ? "האתגר מתחיל היום"
      : daysLeft === 1
      ? "האתגר מתחיל מחר"
      : `האתגר מתחיל בעוד ${hebrewDaysPhrase(daysLeft)}`;

  const sub = challengeTitle
    ? `התכוננו להתחלה של "${challengeTitle}"`
    : "אנחנו במצב הכנה — התוכן ייפתח בהתחלת האתגר";

  return (
    <div
      className="my-4 rounded-2xl border border-border bg-card px-5 py-4 text-right"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 flex-row-reverse">
        <div className="shrink-0 mt-1">
          <CalendarClock className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-foreground">{headline}</h2>
          <p className="text-sm text-muted-foreground mt-1">{sub}</p>
        </div>
      </div>
    </div>
  );
});
